// The dashboard (CR_20260924_Dashboard_Revamp §3): one status card that counts every asset once, then Needs action,
// By place, Movements and — managers only — Utilisation. Every card loads and fails on its own. What each role sees
// comes from the server (needs_action, RLS on the places and counts); the page only picks the wording.
import { placeKind } from '@fleetspoke/core/places';
import {
  palette,
  type AssetDisplayState,
  type PaletteKey,
  stateChips,
} from '@fleetspoke/ui/tokens';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import {
  Box,
  Button,
  Chip,
  Grid,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import { canManage, isSupporting, seesProjects, useSession } from '../auth/session';
import { Card, CardHeader } from '../components/asset/Card';
import { MovementBars, PercentBars } from '../components/Charts';
import { asPlaces } from '../components/NewTransferDialog';
import { RequestAssets, type RequestableProject } from '../components/requests';
import { Loading, LoadError, Page } from '../components/Page';
import {
  type ActionItem,
  dashboardKeys,
  useAssetStateCounts,
  useMovementsPerDay,
  useNeedsAction,
  useUtilisation,
  type Utilisation,
} from '../data/dashboard';
import { activeSites, keys, type SiteOverview, useSites, type Whoami } from '../data/queries';
import { useProjects } from '../data/billing';
import { useLiveInvalidate } from '../data/realtime';
import { useTransferPlaces } from '../data/workflow';
import { isoDateIn } from '../lib/billing';
import {
  ACTION_GROUP_LABEL,
  ACTION_GROUPS,
  type ActionGroup,
  actionGroup,
  actionLink,
  actionParts,
  actionText,
  sortActions,
} from '../lib/dashboard';
import { placePath } from '../lib/places';
import { relativeTime } from '../lib/time';
import { canActAt } from '../lib/zones';

/** Owner to confirm (§3.6): "Request assets" in a project user's dashboard header. One line to remove it. */
const SHOW_REQUEST_ON_DASHBOARD = true;

/** Rows shown before "Show all" (§3.3) and per By place group (§3.4). */
const ACTION_ROWS = 8;
const PLACE_ROWS = 6;

type Mode = 'light' | 'dark';
const colour = (key: PaletteKey, mode: Mode) => palette[key][mode];

/**
 * `canActAt` trusts `platform_admin`, which whoami keeps true during a support visit: a super admin acting as warehouse
 * staff would read as working everywhere. The dashboard follows the acting role, so the flag counts only outside a visit.
 */
function actingMe(me: Whoami | undefined): Whoami | undefined {
  return me && isSupporting(me) ? { ...me, platform_admin: false } : me;
}

// ---------------------------------------------------------------- status card (§3.2)

/**
 * Always shown, then the rest only when some asset is in that state. Bar and tiles share this order. "QR only"
 * (`untagged`) has no tile or bar segment (owner, 2026-09-30); those assets still count in the headline total.
 */
const ALWAYS: AssetDisplayState[] = ['on_site', 'awaiting_receipt', 'in_transit', 'missing'];
const SOMETIMES: AssetDisplayState[] = ['off_site', 'unknown'];

function headlineKey(me: Whoami | undefined): string {
  if (canManage(me)) return 'dashboard.total_registered';
  if (me?.role === 'project_user') return 'dashboard.total_projects';
  return seesProjects(me) ? 'dashboard.total_places' : 'dashboard.total_warehouses';
}

function StatusCard({ me }: { me: Whoami | undefined }) {
  const { t } = useTranslation();
  const mode = useTheme().palette.mode;
  const counts = useAssetStateCounts();
  if (counts.error)
    return (
      <Card>
        <LoadError error={counts.error} />
      </Card>
    );
  if (counts.isLoading || !counts.data)
    return (
      <Card data-testid="status-card-loading">
        <Skeleton variant="rounded" height={96} />
      </Card>
    );
  const by = counts.data;
  const n = (s: AssetDisplayState) => by[s] ?? 0;
  const states = [...ALWAYS, ...SOMETIMES.filter((s) => n(s) > 0)];
  const total = Object.values(by).reduce((sum, v) => sum + v, 0);
  const label = (s: AssetDisplayState) => t(stateChips[s].labelKey);
  return (
    <Card data-testid="status-card">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'auto minmax(0, 1fr)' },
          gap: { xs: 2, sm: 3.5 },
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            pr: { sm: 3.5 },
            borderRight: { sm: 1 },
            borderColor: { sm: 'divider' },
            display: { xs: 'flex', sm: 'block' },
            alignItems: 'baseline',
            flexWrap: 'wrap',
            columnGap: 1.25,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 34, sm: 44 },
              fontWeight: 600,
              lineHeight: 1,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
            }}
            data-testid="status-total"
          >
            {total}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: { sm: 0.75 } }} data-testid="status-label">
            {t(headlineKey(me))}
          </Typography>
          <Link
            component={RouterLink}
            to="/assets"
            underline="hover"
            sx={{
              display: 'inline-block',
              mt: { sm: 1.25 },
              ml: { xs: 'auto', sm: 0 },
              fontSize: 13,
              fontWeight: 500,
              color: colour('accentInk', mode),
            }}
          >
            {t('dashboard.view_all_assets')} →
          </Link>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box
            role="img"
            aria-label={states.map((s) => `${label(s)} ${n(s)}`).join(', ')}
            sx={{
              display: 'flex',
              gap: '2px',
              height: 14,
              borderRadius: 7,
              overflow: 'hidden',
              bgcolor: colour('neutralSoft', mode),
            }}
            data-testid="status-bar"
          >
            {states
              .filter((s) => n(s) > 0)
              .map((s) => (
                <Box
                  key={s}
                  title={`${label(s)}: ${n(s)}`}
                  sx={{ flex: n(s), bgcolor: colour(stateChips[s].fg, mode) }}
                />
              ))}
          </Box>
          <Box
            sx={{
              display: 'grid',
              gap: 1,
              mt: 1.75,
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(4, minmax(0, 1fr))',
                lg: `repeat(${states.length}, minmax(0, 1fr))`,
              },
            }}
          >
            {states.map((s) => {
              const alert = s === 'missing' && n(s) > 0;
              return (
                <Link
                  key={s}
                  component={RouterLink}
                  to={`/assets?state=${s}`}
                  underline="none"
                  color="inherit"
                  data-testid={`state-tile-${s}`}
                  data-alert={alert ? 'true' : undefined}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.25,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 2,
                    border: 1,
                    borderColor: alert ? colour('bad', mode) : 'divider',
                    bgcolor: alert ? colour('badSoft', mode) : undefined,
                    opacity: n(s) === 0 ? 0.55 : 1,
                    '&:hover': { bgcolor: alert ? undefined : 'action.hover' },
                  }}
                >
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: 0.5,
                        flex: 'none',
                        bgcolor: colour(stateChips[s].fg, mode),
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                      {label(s)}
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{
                      fontSize: 22,
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                      color: alert ? colour('bad', mode) : undefined,
                    }}
                  >
                    {n(s)}
                  </Typography>
                </Link>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Card>
  );
}

// ---------------------------------------------------------------- needs action (§3.3)

const GROUP_ICON: Record<ActionGroup, ReactNode> = {
  missing: <ErrorOutlineIcon fontSize="small" />,
  transfers: <SwapHorizIcon fontSize="small" />,
  counts: <FactCheckOutlinedIcon fontSize="small" />,
  requests: <AssignmentOutlinedIcon fontSize="small" />,
  certificates: <VerifiedOutlinedIcon fontSize="small" />,
  devices: <SensorsOutlinedIcon fontSize="small" />,
  projects: <EngineeringOutlinedIcon fontSize="small" />,
};
const SEVERITY_TONE: Record<ActionItem['severity'], { fg: PaletteKey; bg: PaletteKey }> = {
  critical: { fg: 'bad', bg: 'badSoft' },
  warning: { fg: 'warn', bg: 'warnSoft' },
  info: { fg: 'steel', bg: 'steelSoft' },
};

function CountBadge({ count, hot }: { count: number; hot: boolean }) {
  const mode = useTheme().palette.mode;
  return (
    <Box
      component="span"
      data-testid="needs-action-count"
      data-hot={hot ? 'true' : undefined}
      sx={{
        display: 'inline-grid',
        placeItems: 'center',
        minWidth: 22,
        height: 22,
        px: 0.875,
        ml: 0.75,
        borderRadius: 11,
        fontSize: 12,
        fontWeight: 600,
        verticalAlign: 1,
        bgcolor: colour(hot ? 'badSoft' : 'warnSoft', mode),
        color: colour(hot ? 'bad' : 'warn', mode),
      }}
    >
      {count}
    </Box>
  );
}

function NeedsActionCard({ me }: { me: Whoami | undefined }) {
  const { t, i18n } = useTranslation();
  const mode = useTheme().palette.mode;
  const actions = useNeedsAction();
  const sites = useSites();
  const places = useTransferPlaces(asPlaces(sites.data));
  const [filter, setFilter] = useState<ActionGroup | 'all'>('all');
  const [showAll, setShowAll] = useState(false);
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';
  const acting = actingMe(me);
  const viewer = {
    manager: canManage(me),
    atSite: (siteId: string | null) => canActAt(acting, siteId),
    today: isoDateIn(tz),
    placeName: (siteId: string | null) => places.data?.find((p) => p.site_id === siteId)?.name,
  };
  const items = sortActions(actions.data ?? []);
  const groups = ACTION_GROUPS.filter((g) => items.some((a) => actionGroup(a.kind) === g));
  const current = filter !== 'all' && groups.includes(filter) ? filter : 'all';
  const shown = items.filter((a) => current === 'all' || actionGroup(a.kind) === current);
  const visible = showAll ? shown : shown.slice(0, ACTION_ROWS);
  const now = Date.now();
  const chip = (key: ActionGroup | 'all', label: string, count: number) => {
    const on = current === key;
    return (
      <Chip
        key={key}
        size="small"
        label={`${label} ${count}`}
        variant={on ? 'filled' : 'outlined'}
        onClick={() => {
          setFilter(key);
          setShowAll(false);
        }}
        aria-pressed={on}
        data-testid={`action-filter-${key}`}
        sx={
          on
            ? {
                bgcolor: 'text.primary',
                color: 'background.paper',
                '&:hover': { bgcolor: 'text.primary' },
              }
            : undefined
        }
      />
    );
  };

  return (
    <Card role="region" aria-labelledby="dash-actions">
      <CardHeader
        title={
          <span id="dash-actions">
            {t('dashboard.needs_action')}
            {items.length ? (
              <CountBadge count={items.length} hot={items.some((a) => a.severity === 'critical')} />
            ) : null}
          </span>
        }
      />
      {actions.isLoading ? (
        <Loading />
      ) : actions.error ? (
        <LoadError error={actions.error} />
      ) : !items.length ? (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ py: 1 }}
          data-testid="needs-action-empty"
        >
          <CheckCircleIcon sx={{ color: colour('ok', mode) }} />
          <Typography color="text.secondary">{t('dashboard.nothing')}</Typography>
        </Stack>
      ) : (
        <>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mb: 1.25 }}>
            {chip('all', t('dashboard.f_all'), items.length)}
            {groups.map((g) =>
              chip(
                g,
                t(ACTION_GROUP_LABEL[g]),
                items.filter((a) => actionGroup(a.kind) === g).length,
              ),
            )}
          </Stack>
          <Box
            component="ul"
            data-testid="needs-action"
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2.5,
              overflow: 'hidden',
            }}
          >
            {visible.map((a) => {
              const p = actionParts(a, t, now, i18n.language, viewer);
              const tone = SEVERITY_TONE[a.severity];
              return (
                <Box
                  component="li"
                  key={`${a.kind}:${a.ref_id}`}
                  sx={{ '& + &': { borderTop: 1, borderColor: 'divider' } }}
                >
                  <Link
                    component={RouterLink}
                    to={actionLink(a)}
                    underline="none"
                    color="inherit"
                    aria-label={actionText(a, t, relativeTime(a.since, i18n.language))}
                    data-testid={`action-${a.kind}`}
                    data-severity={a.severity}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '4px 28px minmax(0, 1fr) 14px',
                        sm: '4px 28px minmax(0, 1fr) auto 14px',
                      },
                      columnGap: 1.5,
                      alignItems: 'center',
                      py: 1.375,
                      pr: 1.75,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box
                      sx={{ alignSelf: 'stretch', my: -1.375, bgcolor: colour(tone.fg, mode) }}
                    />
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1.75,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: colour(tone.bg, mode),
                        color: colour(tone.fg, mode),
                      }}
                    >
                      {GROUP_ICON[p.group]}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 11,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          fontWeight: 500,
                          color: 'text.secondary',
                        }}
                      >
                        {p.kind}
                      </Typography>
                      <Typography sx={{ fontWeight: 500 }}>{p.title}</Typography>
                      {p.detail ? (
                        <Typography variant="body2" color="text.secondary">
                          {p.detail}
                        </Typography>
                      ) : null}
                    </Box>
                    <Box
                      sx={{
                        gridColumn: { xs: 3, sm: 'auto' },
                        gridRow: { xs: 2, sm: 'auto' },
                        textAlign: { xs: 'left', sm: 'right' },
                        whiteSpace: 'nowrap',
                        fontSize: 12.5,
                        color: 'text.secondary',
                        mt: { xs: 0.5, sm: 0 },
                      }}
                      data-testid="action-age"
                    >
                      <Box
                        component="b"
                        sx={{
                          display: { xs: 'inline', sm: 'block' },
                          mr: { xs: 0.75, sm: 0 },
                          fontWeight: 600,
                          fontSize: 12,
                          color: a.severity === 'info' ? undefined : colour(tone.fg, mode),
                        }}
                      >
                        {p.age}
                      </Box>
                      {p.ageNote}
                    </Box>
                    <ChevronRightIcon
                      sx={{
                        fontSize: 16,
                        color: colour('neutral', mode),
                        gridColumn: { xs: 4, sm: 'auto' },
                        gridRow: { xs: 1, sm: 'auto' },
                      }}
                    />
                  </Link>
                </Box>
              );
            })}
          </Box>
          {shown.length > ACTION_ROWS && !showAll ? (
            <Button size="small" onClick={() => setShowAll(true)} sx={{ mt: 1 }}>
              {t('dashboard.show_all', { count: shown.length })}
            </Button>
          ) : null}
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- by place (§3.4)

function Pill({
  tone,
  children,
}: {
  tone: 'bad' | 'warn' | 'ok' | 'await' | 'mute';
  children: ReactNode;
}) {
  const mode = useTheme().palette.mode;
  const [fg, bg]: [PaletteKey | null, PaletteKey] = {
    bad: ['bad', 'badSoft'] as [PaletteKey, PaletteKey],
    warn: ['warn', 'warnSoft'] as [PaletteKey, PaletteKey],
    ok: ['ok', 'okSoft'] as [PaletteKey, PaletteKey],
    await: ['accentInk', 'accentSoft'] as [PaletteKey, PaletteKey],
    mute: [null, 'neutralSoft'] as [null, PaletteKey],
  }[tone];
  return (
    <Box
      component="span"
      data-pill={tone}
      sx={{
        fontSize: 11.5,
        fontWeight: 500,
        px: 1,
        py: '1px',
        borderRadius: 2.5,
        whiteSpace: 'nowrap',
        bgcolor: colour(bg, mode),
        color: fg ? colour(fg, mode) : 'text.secondary',
      }}
    >
      {children}
    </Box>
  );
}

/** Missing first, then the biggest shortfall, then by name. */
function byAttention(a: SiteOverview, b: SiteOverview): number {
  return (
    Number(b.missing > 0) - Number(a.missing > 0) ||
    b.expected - b.on_site - (a.expected - a.on_site) ||
    a.name.localeCompare(b.name)
  );
}

function PlaceRow({ s }: { s: SiteOverview }) {
  const { t } = useTranslation();
  const mode = useTheme().palette.mode;
  const short = s.expected - s.on_site;
  const pct = (v: number) => (s.expected ? `${Math.min(100, (v / s.expected) * 100)}%` : '0%');
  const gw =
    s.gateways > 0
      ? s.gateways_online === s.gateways
        ? 'ok'
        : s.gateways_online > 0
          ? 'warn'
          : 'bad'
      : null;
  return (
    <Link
      component={RouterLink}
      to={placePath(s)}
      underline="none"
      color="inherit"
      data-testid={`place-${s.site_id}`}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gap: '6px 12px',
        alignItems: 'center',
        py: 1.25,
        px: 1,
        mx: -1,
        borderRadius: 2,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        {gw ? (
          <Tooltip
            title={t('dashboard.gateways_online', { online: s.gateways_online, total: s.gateways })}
          >
            <Box
              data-testid="gateway-dot"
              data-state={gw}
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                flex: 'none',
                bgcolor: colour(gw, mode),
              }}
            />
          </Tooltip>
        ) : null}
        <Typography sx={{ fontWeight: 500 }} noWrap>
          {s.name}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
        {s.expected ? (
          <>
            <Box component="b" sx={{ color: 'text.primary', fontWeight: 600, fontSize: 14 }}>
              {s.on_site}
            </Box>{' '}
            / {s.expected}
          </>
        ) : (
          '—'
        )}
      </Typography>
      <Box
        sx={{
          gridColumn: '1 / -1',
          display: 'flex',
          height: 6,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: colour('neutralSoft', mode),
        }}
      >
        {s.expected ? (
          <>
            <Box sx={{ width: pct(s.on_site), bgcolor: colour('ok', mode) }} />
            {s.missing ? (
              <Box sx={{ width: pct(s.missing), bgcolor: colour('bad', mode) }} />
            ) : null}
          </>
        ) : null}
      </Box>
      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        spacing={0.75}
        sx={{ gridColumn: '1 / -1' }}
      >
        {s.expected === 0 ? (
          <Pill tone="mute">{t('dashboard.p_no_assets')}</Pill>
        ) : short > 0 ? (
          <Pill tone="warn">{t('dashboard.p_not_here', { count: short })}</Pill>
        ) : (
          <Pill tone="ok">{t('dashboard.p_all_here')}</Pill>
        )}
        {s.missing > 0 ? (
          <Pill tone="bad">{t('dashboard.p_missing', { count: s.missing })}</Pill>
        ) : null}
        {s.awaiting_receipt > 0 ? (
          <Pill tone="await">{t('dashboard.p_to_receive', { count: s.awaiting_receipt })}</Pill>
        ) : null}
      </Stack>
    </Link>
  );
}

function PlaceGroup({
  title,
  rows,
  more,
  testId,
}: {
  title: string;
  rows: SiteOverview[];
  more: { to: string; label: string };
  testId: string;
}) {
  const mode = useTheme().palette.mode;
  const sorted = [...rows].sort(byAttention);
  return (
    <Box data-testid={testId} sx={{ '& + &': { mt: 1.75 } }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          fontSize: 11,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: colour('neutral', mode),
          mb: 0.5,
        }}
      >
        <span>{title}</span>
        <span>{rows.length}</span>
      </Stack>
      {sorted.slice(0, PLACE_ROWS).map((s) => (
        <PlaceRow key={s.site_id} s={s} />
      ))}
      {rows.length > PLACE_ROWS ? (
        <Link
          component={RouterLink}
          to={more.to}
          underline="hover"
          sx={{ fontSize: 13, fontWeight: 500, color: colour('accentInk', mode) }}
        >
          {more.label} →
        </Link>
      ) : null}
    </Box>
  );
}

function ByPlaceCard() {
  const { t } = useTranslation();
  const sites = useSites();
  const rows = activeSites(sites.data);
  const warehouses = rows.filter((s) => placeKind(s.type) === 'warehouse');
  const locations = rows.filter((s) => placeKind(s.type) !== 'warehouse');
  return (
    <Card role="region" aria-labelledby="dash-places">
      <CardHeader title={<span id="dash-places">{t('dashboard.by_place')}</span>} />
      {sites.isLoading ? (
        <Loading />
      ) : sites.error ? (
        <LoadError error={sites.error} />
      ) : !rows.length ? (
        <Typography color="text.secondary">{t('sites.empty')}</Typography>
      ) : (
        <>
          {/* a group with no rows is hidden: a project user gets no warehouse rows, so no Warehouses heading */}
          {warehouses.length ? (
            <PlaceGroup
              title={t('dashboard.group_warehouses')}
              rows={warehouses}
              more={{ to: '/warehouses', label: t('dashboard.all_warehouses') }}
              testId="places-warehouses"
            />
          ) : null}
          {locations.length ? (
            <PlaceGroup
              title={t('dashboard.group_projects')}
              rows={locations}
              more={{ to: '/projects', label: t('dashboard.all_projects') }}
              testId="places-projects"
            />
          ) : null}
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- movements and utilisation (§3.5)

function MovementsCard() {
  const { t } = useTranslation();
  const movements = useMovementsPerDay(30);
  return (
    <Card role="region" aria-labelledby="dash-movements">
      <CardHeader
        title={<span id="dash-movements">{t('dashboard.movements_title')}</span>}
        aside={
          <Typography variant="caption" color="text.secondary">
            {t('dashboard.last_30_days')}
          </Typography>
        }
      />
      {movements.isLoading ? (
        <Loading />
      ) : movements.error ? (
        <LoadError error={movements.error} />
      ) : (
        <MovementBars days={movements.data ?? []} />
      )}
    </Card>
  );
}

function UtilisationCard({ from, to }: { from: string; to: string }) {
  const { t } = useTranslation();
  const mode = useTheme().palette.mode;
  const utilisation = useUtilisation(from, to, true);
  const rows: Utilisation[] = utilisation.data ?? [];
  const label = (u: Utilisation) => u.category_name || t('dashboard.no_category');
  const empty = rows.every((u) => !Number(u.utilisation ?? 0));
  return (
    <Card role="region" aria-labelledby="dash-utilisation">
      <CardHeader
        title={<span id="dash-utilisation">{t('dashboard.utilisation_title')}</span>}
        aside={
          <Typography variant="caption" color="text.secondary">
            {t('dashboard.days_30')}
          </Typography>
        }
      />
      {utilisation.isLoading ? (
        <Loading />
      ) : utilisation.error ? (
        <LoadError error={utilisation.error} />
      ) : empty ? (
        <Stack
          direction="row"
          spacing={1.75}
          alignItems="flex-start"
          data-testid="utilisation-empty"
          sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2.5 }}
        >
          <BarChartIcon sx={{ color: colour('neutral', mode) }} />
          <Box>
            <Typography sx={{ fontWeight: 500 }}>{t('dashboard.util_empty_title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.util_empty_body')}
            </Typography>
            {rows.length ? (
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mt: 1.25 }}>
                {rows.map((u) => (
                  <Pill key={u.category_id ?? 'none'} tone="mute">
                    {label(u)} 0 %
                  </Pill>
                ))}
              </Stack>
            ) : null}
          </Box>
        </Stack>
      ) : (
        <PercentBars
          empty=""
          items={[...rows]
            .sort((a, b) => Number(b.utilisation ?? 0) - Number(a.utilisation ?? 0))
            .map((u) => ({
              key: u.category_id ?? 'none',
              label: label(u),
              value: Number(u.utilisation ?? 0),
              detail: t('dashboard.utilisation_detail', {
                deployed: u.deployed_days,
                total: u.asset_days,
                assets: u.assets,
              }),
              to: '/reports?r=utilisation',
            }))}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- Request assets (§3.6)

/** The projects whose active locations the viewer works at, each with those locations, by name. */
function useRequestableProjects(me: Whoami | undefined): RequestableProject[] {
  const sites = useSites();
  const projects = useProjects();
  const acting = actingMe(me);
  const names = new Map((projects.data ?? []).map((p) => [p.id, p.name]));
  const byProject = new Map<string, SiteOverview[]>();
  for (const s of activeSites(sites.data)) {
    if (placeKind(s.type) === 'warehouse' || !s.project_id || !canActAt(acting, s.site_id))
      continue;
    byProject.set(s.project_id, [...(byProject.get(s.project_id) ?? []), s]);
  }
  return [...byProject]
    .filter(([id]) => names.has(id))
    .map(([id, locations]) => ({ id, name: names.get(id)!, locations }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function DashboardRequestButton({ me }: { me: Whoami | undefined }) {
  return <RequestAssets projects={useRequestableProjects(me)} variant="contained" />;
}

// ---------------------------------------------------------------- the page

export function DashboardPage() {
  const { t } = useTranslation();
  const { me } = useSession();
  const manager = canManage(me);
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';
  const today = isoDateIn(tz);
  const from30 = isoDateIn(tz, new Date(Date.now() - 29 * 86_400_000));
  useLiveInvalidate(
    'dashboard',
    ['asset_presence', 'transfers', 'gateways', 'stock_counts', 'alerts'],
    [keys.sites, dashboardKeys.stateCounts, dashboardKeys.actions],
  );

  return (
    <Page
      title={t('dashboard.title')}
      actions={
        // not for managers, not for warehouse staff without projects; the button hides itself with no location
        SHOW_REQUEST_ON_DASHBOARD &&
        !manager &&
        (me?.role === 'project_user' || seesProjects(me)) ? (
          <DashboardRequestButton me={me} />
        ) : undefined
      }
    >
      <Stack spacing={2}>
        <StatusCard me={me} />
        {manager ? (
          <Grid container spacing={2} alignItems="flex-start">
            <Grid size={{ xs: 12, md: 7 }}>
              <NeedsActionCard me={me} />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <ByPlaceCard />
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <MovementsCard />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <UtilisationCard from={from30} to={today} />
            </Grid>
          </Grid>
        ) : (
          // no Utilisation card: Movements stacks under By place, so there is no empty half row
          <Grid container spacing={2} alignItems="flex-start">
            <Grid size={{ xs: 12, md: 7 }}>
              <NeedsActionCard me={me} />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Stack spacing={2}>
                <ByPlaceCard />
                <MovementsCard />
              </Stack>
            </Grid>
          </Grid>
        )}
      </Stack>
    </Page>
  );
}
